import { isEmpty } from "lodash";

export const getSubdomainAndMainDomain = (url) => {
  const cleanUrl = url.replace(/(^\w+:|^)\/\//, "");
  const domain = cleanUrl.startsWith("www.") ? cleanUrl.substring(4) : cleanUrl;

  const parts = domain.split(".");

  if (parts.length > 2) {
    const subdomain = parts.slice(0, -2).join("");
    const mainDomain = parts.slice(-2, -1)[0];
    return `${subdomain}${mainDomain}`;
  }

  return parts[0];
};

export const dateFormatter = (dateString, format) => {
  const date = new Date(dateString);
  const formatParts = {
    DD: String(date.getDate()).padStart(2, "0"),
    MM: String(date.getMonth() + 1).padStart(2, "0"),
    YYYY: date.getFullYear(),
    HH: String(date.getHours() % 12 || 12).padStart(2, "0"),
    mm: String(date.getMinutes()).padStart(2, "0"),
    ss: String(date.getSeconds()).padStart(2, "0"),
    AMPM: date.getHours() >= 12 ? "PM" : "AM",
  };
  return format.replace(
    /DD|MM|YYYY|HH|mm|ss|AMPM/g,
    (match) => formatParts[match]
  );
};

export const rolesSelectorBasedOnHierarchy = (role, rolesArray) => {
  const currentRoleIndex = rolesArray.findIndex((r) => r.value === role);

  return rolesArray.filter((r, index) => index <= currentRoleIndex);
};

export const convertUnderscoreToSpaces = (str) => {
  if (typeof str !== "string" || str === null || str === undefined) {
    return "";
  }

  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getStringParamsFromObject = (params) => {
  return Object.keys(params)
    .filter((key) => typeof params[key] === "number" || !isEmpty(params[key]))
    .map((key) => key + "=" + encodeURIComponent(params[key]))
    .join("&");
};

// converts date string format: 2024-12-18T05:58:29.349Z to format like: Aug 4, 2024 at 07:30pm
export const formatDate = (dateString) => {
  const date = new Date(dateString);

  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };

  return date.toLocaleString("en-US", options);
};

// Function to get status color based on status
export const getStatusColor = (status) => {
  switch (status) {
    case "pending_review":
      return "warning";
    case "pending_sitemap_generation":
      return "info";
    case "sitemap_generated":
      return "secondary";
    case "to_be_indexed":
      return "warning";
    case "sent_for_indexing":
      return "info";
    case "indexed":
      return "success";
    case "to_be_skipped":
      return "default";
    case "sent_for_skipping":
      return "info";
    case "skipped":
      return "default";
    case "to_be_deleted":
      return "info";
    case "sent_for_deletion":
      return "info";
    case "deleted":
      return "error";
    case "failed_to_index":
      return "error";
    case "failed_to_delete":
      return "error";
    case "failed_to_skip":
      return "error";
    default:
      return "default";
  }
};

// Function to get Chip properties based on status
const resolveStatusStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("fail") || s.includes("error"))
    return { bg: "#fee2e2", color: "#dc2626", border: "#fecaca" };
  if (s === "indexed" || s.includes("generated") || s === "active" || s === "completed")
    return { bg: "#dcfce7", color: "#16a34a", border: "#bbf7d0" };
  if (s.startsWith("sent_for") || s.includes("sending") || s.includes("in_progress"))
    return { bg: "#e0e7ff", color: "#4338ca", border: "#c7d2fe" };
  if (s.startsWith("to_be"))
    return { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" };
  if (s.includes("pending"))
    return { bg: "#fef9c3", color: "#a16207", border: "#fde68a" };
  if (s.includes("skip"))
    return { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0" };
  if (s.includes("delet") || s.includes("removed"))
    return { bg: "#fee2e2", color: "#dc2626", border: "#fecaca" };
  return { bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };
};

export const getStatusChipProps = (status) => {
  const { bg, color, border } = resolveStatusStyle(status);
  return {
    label: convertUnderscoreToSpaces(status),
    sx: {
      bgcolor: bg,
      color,
      border: `1px solid ${border}`,
      fontWeight: 600,
      fontSize: "0.75rem",
      height: 24,
      borderRadius: "6px",
      "& .MuiChip-label": { px: "8px" },
    },
  };
};

// Removes duplicates from an array
export const removeDuplicatesFromArray = (arr) => {
  return arr.reduce((acc, item) => {
    if (!acc.includes(item)) {
      acc.push(item);
    }
    return acc;
  }, []);
};

export const generateTempId = () => `temp-${Date.now()}-${Math.random()}`;

export const yearsListSince = (date) => {
  const currentYear = new Date().getFullYear();
  const startYear = new Date(date).getFullYear();
  const years = [];
  for (let i = startYear; i <= currentYear; i++) {
    years.push(i.toString(10));
  }
  return years;
};

export const getRedirectUrl = (app, urlPart, env, triggerPoint = null) => {
  if (app) {
    // eslint-disable-next-line no-undef
    let urlEnv = env || process.env.REACT_APP_CURRENT_ENVIRONMENT;
    return (
      (urlEnv === "production"
        ? `https://${app.url_code}-${app.country}.backoffice.shopinsync.com`
        : urlEnv === "staging"
        ? // eslint-disable-next-line no-undef
          process.env.REACT_APP_STAGE_BACKOFFICE_URL
        : // eslint-disable-next-line no-undef
          process.env.REACT_APP_DEV_BACKOFFICE_URL) +
      urlPart +
      `?market=${app.market}` +
      (triggerPoint ? `&triggerPoint=${triggerPoint}` : "")
    );
  }
  return "/";
};

/**
 * Exports data to a CSV file.
 * @param {*} data - The data to export.
 * @param {*} filename - The name of the file to export.
 * @returns {void}
 */
export const exportToCSV = (data, filename = "export.csv", customHeaders) => {
  if (!data || data.length === 0) {
    return;
  }

  const headers = customHeaders || Object.keys(data[0]);
  const csvContent = [
    headers.join(","),
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          if (value === null || value === undefined) {
            return "";
          }
          const stringValue = String(value);
          if (
            stringValue.includes(",") ||
            stringValue.includes('"') ||
            stringValue.includes("\n")
          ) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const formatIndexedTimestamp = (utcTimestamp) => {
  if (!utcTimestamp) return null;

  return new Date(utcTimestamp).toLocaleString("en-IN", {
    timeZoneName: "short",
    hour12: true,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Splits a raw URL string into its domain and path parts.
 * Strips "www." from the hostname and trailing slashes from the path.
 * Used in UrlsSidebar and UnifiedUrlsSidebar to render URL chips with
 * the domain dimmed and the path highlighted separately.
 *
 * @param {string} rawUrl - A full URL string (e.g. "https://www.example.com/foo/bar?q=1")
 * @returns {{ domain: string, path: string }}
 *   - domain: hostname without "www." (e.g. "example.com")
 *   - path: pathname + search string, no trailing slash (e.g. "/foo/bar?q=1"), or "/" for root
 *   - Falls back to { domain: rawUrl, path: "" } if the URL is unparseable.
 */
export function parseUrl(rawUrl) {
  try {
    const u = new URL(rawUrl);
    const domain = u.hostname.replace(/^www\./, "");
    const path = (u.pathname + u.search).replace(/\/$/, "") || "/";
    return { domain, path };
  } catch {
    return { domain: rawUrl, path: "" };
  }
}
